import { useWsmPlugin } from "./useWsmPlugin";
import useSWR from "swr";
import usePairedDevice from "./usePairedDevice";
import { useCallback, useEffect } from "react";
import {
  WSMRemoteCfgStatusCfgApp,
  WSMRemoteCfgStatusCfgAppRadar,
} from "@WahooFitness/wsm-native/dist/esm/types/remote_config_cfg_status";
import { useGuardedUpdater } from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import { WSMSensorProductTypeInfo } from "@WahooFitness/wsm-native/dist/esm/types/sensor_product_type_info";
import {
  WSMRemoteSensor,
  WSMRemoteSensorCfg,
} from "@WahooFitness/wsm-native/dist/esm/types/remote_sensor_status";
import { useProductTypeHelper } from "./useProductTypeHelper";
import useSWRImmutable from "swr/immutable";
import WSMSensorProductType from "@WahooFitness/wsm-native/dist/esm/types/product_type";
import WSMSensorLocation from "@WahooFitness/wsm-native/dist/esm/types/sensor_location";
import { useTheme } from "@mui/material";
import { crux_sensor_product_type_e } from "@WahooFitness/crux-js-display-cfg";

type ProductTypeInfo = {
  name: string;
  manufacturerName: string;
  icon: string;
  iconMulti?: string;
  cfg: WSMSensorProductTypeInfo;
};

const useRemoteSensors = (appToken?: string) => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { device } = usePairedDevice(appToken);
  const { update } = useGuardedUpdater();
  const { productTypeHelper } = useProductTypeHelper();
  const { palette } = useTheme();

  const { data: remoteSensorStatus, mutate: refreshRemoteSensorStatus } = useSWR(
    appToken && device && ["remoteSensors", appToken],
    async () => {
      if (device) {
        const result = await sensorManagerPlugin?.getRemoteSensorStatus({
          sensorId: device.sensorId,
        });
        if (!result) {
          throw new Error("Failed to get remote sensor status");
        }
        return result.result;
      }
    }
  );

  const { data: productTypeInfoLookup } = useSWRImmutable(
    remoteSensorStatus !== undefined && [
      "productTypeInfoLookup",
      palette.mode,
      [
        ...new Set(
          remoteSensorStatus.sensors
            .filter((sensor) => sensor.status.is_paired)
            .map((s) => s.status.sensor_product_type)
            .sort()
        ),
      ], // stable key
    ],
    async ([_key, _mode, productTypes]) => {
      const productInfoList = await Promise.all(
        productTypes.map(async (productType) =>
          sensorManagerPlugin?.getSensorProductTypeInfo({
            sensorProductType: productType,
            iconHeightPx: 100,
          })
        )
      );
      return productInfoList.reduce(
        (acc, productInfo, index) => {
          acc[productTypes[index]] = productInfo;
          return acc;
        },
        {} as Record<number, ProductTypeInfo>
      );
    }
  );

  const { data: remoteSensorAppConfig, mutate: mutateRemoteSensorAppConfig } = useSWR(
    device?.appToken !== undefined &&
      device?.sensorId !== undefined && ["remoteSensorAppConfig", device.sensorId, device.appToken],
    async ([_key, sensorId]) => {
      const result = await sensorManagerPlugin?.getRemoteCfgStatus({ sensorId });
      return result.result;
    }
  );

  const getRemoteSensor = useCallback(
    (remoteSensorId: number) => {
      return remoteSensorStatus?.sensors.find(
        (sensor) => sensor.remote_sensor_id[0] === remoteSensorId
      );
    },
    [remoteSensorStatus?.sensors]
  );

  const saveDisplayName = useCallback(
    async (remoteSensorId: number, newDisplayName: string) => {
      update(
        async () => {
          if (!device?.appToken) {
            return;
          }
          const result = await sensorManagerPlugin.sendSetRemoteSensorCfg({
            sensorId: device.sensorId,
            appToken: device.appToken,
            remote_sensor_id: [remoteSensorId],
            "#comment": `Display name: ${newDisplayName}`,
            cfg: {
              display_name: newDisplayName,
              sensor_id: remoteSensorId,
            },
          });
          if (!result.completed) {
            throw new Error("Failed to save display name");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result && remoteSensorStatus) {
              const newSensors = remoteSensorStatus.sensors.map((sensor) => {
                if (sensor.remote_sensor_id[0] === remoteSensorId) {
                  return { ...sensor, cfg: { ...sensor.cfg, display_name: newDisplayName } };
                }
                return sensor;
              });
              refreshRemoteSensorStatus(
                {
                  ...remoteSensorStatus,
                  sensors: newSensors,
                },
                { revalidate: false }
              );
            }
            return result;
          },
          errorMessage: t`Failed to change display name`,
        }
      );
    },
    [
      device?.appToken,
      device?.sensorId,
      refreshRemoteSensorStatus,
      remoteSensorStatus,
      sensorManagerPlugin,
      update,
    ]
  );

  const getProductType = useCallback((remoteSensor: WSMRemoteSensor | undefined) => {
    if (!remoteSensor) {
      return;
    }
    return remoteSensor?.status?.sensor_product_type;
  }, []);

  const getDisplayName = useCallback(
    (remoteSensor: WSMRemoteSensor | undefined) => {
      if (!remoteSensor) {
        return "";
      }
      if (remoteSensor.cfg?.display_name && remoteSensor.cfg.display_name != "") {
        return remoteSensor.cfg.display_name;
      }
      if (!remoteSensor.status?.sensor_product_type) {
        return `Sensor ${remoteSensor.remote_sensor_id[0]}`;
      }
      const localizedName = productTypeInfoLookup?.[remoteSensor.status.sensor_product_type]?.name;
      if (localizedName) {
        return localizedName;
      }
      return (
        productTypeHelper?.string(remoteSensor?.status.sensor_product_type) ??
        `Sensor type ${remoteSensor.status.sensor_product_type}`
      );
    },
    [productTypeHelper, productTypeInfoLookup]
  );

  const getImageSrc = useCallback(
    (remoteSensor: WSMRemoteSensor | undefined) => {
      if (!remoteSensor?.status?.sensor_product_type) {
        return undefined;
      }
      const imgSrc = productTypeInfoLookup?.[remoteSensor.status.sensor_product_type]?.icon;
      const multiImgSrc =
        productTypeInfoLookup?.[remoteSensor.status.sensor_product_type]?.iconMulti;

      if (multiImgSrc) {
        if (
          remoteSensor?.status.sensor_product_type ==
            crux_sensor_product_type_e.WAHOO_POWER_PEDALS_LEFT &&
          remoteSensor?.sensors[0]?.status?.is_paired
        ) {
          return multiImgSrc;
        }
      }
      return imgSrc;
    },
    [productTypeInfoLookup]
  );

  const getWheelCircumferenceOptions = useCallback(async () => {
    const result = await sensorManagerPlugin?.getWheelCircumferenceCfgOptions({});
    return result?.cfg?.wheels;
  }, [sensorManagerPlugin]);

  const getShifterCfgOptions = useCallback(
    async (remoteSensor: WSMRemoteSensor | undefined) => {
      const productType = getProductType(remoteSensor);
      if (!productType) {
        return;
      }
      const result = await sensorManagerPlugin?.getBikeGearCfgOptions({
        sensorProductType: productType as WSMSensorProductType,
      });
      return result.cfg;
    },
    [getProductType, sensorManagerPlugin]
  );

  const forgetRemoteSensor = useCallback(
    async (remoteSensorId: number) => {
      return await update(
        async () => {
          if (!device) {
            throw new Error("No device");
          }
          const payload = {
            sensorId: device.sensorId,
            remote_sensor_id: [remoteSensorId],
          };
          const result = await sensorManagerPlugin.sendForgetRemoteSensor(payload);
          if (!result.completed) {
            throw new Error("Failed to forget remote sensor");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result && remoteSensorStatus) {
              refreshRemoteSensorStatus(
                {
                  ...remoteSensorStatus,
                  sensors: remoteSensorStatus.sensors.filter(
                    (sensor) => sensor.remote_sensor_id[0] !== remoteSensorId
                  ),
                },
                { revalidate: false }
              );
            }
            return result;
          },
          errorMessage: t`Failed to unpair sensor`,
        }
      );
    },
    [device, refreshRemoteSensorStatus, remoteSensorStatus, sensorManagerPlugin, update]
  );

  const saveRemoteSensorConfig = useCallback(
    async (remoteSensorId: number, sensorConfig: Partial<WSMRemoteSensorCfg>) => {
      const remoteSensor = remoteSensorStatus?.sensors.find(
        (s) => s.remote_sensor_id[0] === remoteSensorId
      );

      if (!remoteSensor) {
        throw new Error("Remote sensor not found");
      }

      return update(
        async () => {
          if (!device?.appToken) {
            throw new Error("No device or app token");
          }
          const newConfig: WSMRemoteSensorCfg = {
            ...remoteSensor.cfg,
            ...sensorConfig,
          };
          const result = await sensorManagerPlugin.sendSetRemoteSensorCfg({
            sensorId: device.sensorId,
            appToken: device.appToken,
            remote_sensor_id: [remoteSensorId],
            "#comment": `Updating sensor config: ${JSON.stringify(sensorConfig)}`,
            cfg: newConfig,
          });
          if (!result.completed) {
            throw new Error("Failed to save sensor configuration");
          }
          return true;
        },
        {
          afterUpdate: (result) => {
            if (result) {
              refreshRemoteSensorStatus();
            }
            return result;
          },
          errorMessage: t`Failed to save sensor configuration`,
        }
      );
    },
    [
      remoteSensorStatus?.sensors,
      update,
      device?.appToken,
      device?.sensorId,
      sensorManagerPlugin,
      refreshRemoteSensorStatus,
    ]
  );

  const saveAppConfig = useCallback(
    async (appConfig: Partial<WSMRemoteCfgStatusCfgApp>) => {
      return update(
        async () => {
          if (!remoteSensorAppConfig) {
            throw new Error("No remoteSensorAppConfig");
          }
          const result = await sensorManagerPlugin.sendSetRemoteAppCfg({
            sensorId: remoteSensorAppConfig?.sensor_id,
            appToken: remoteSensorAppConfig?.app_token,
            "#comment": `Updating: ${JSON.stringify(appConfig)}`,
            remote_sensor_id: [],
            app: appConfig,
          });
          if (!result.completed) {
            throw new Error("Failed to save config");
          }
          return {
            ...remoteSensorAppConfig.cfg.app,
            ...appConfig,
          };
        },
        {
          afterUpdate: (config) => {
            if (remoteSensorAppConfig) {
              mutateRemoteSensorAppConfig(
                {
                  ...remoteSensorAppConfig,
                  cfg: { ...remoteSensorAppConfig.cfg, app: config },
                },
                { revalidate: false }
              );
            }
          },
          errorMessage: t`Failed to save remote sensor changes`,
        }
      );
    },
    [mutateRemoteSensorAppConfig, remoteSensorAppConfig, sensorManagerPlugin, update]
  );

  const saveAppRadarConfig = useCallback(
    async (appRadarConfig: Partial<WSMRemoteCfgStatusCfgAppRadar>) => {
      if (!remoteSensorAppConfig) {
        throw new Error("No remoteSensorAppConfig");
      }
      const newRadarConfig: WSMRemoteCfgStatusCfgAppRadar = {
        ...remoteSensorAppConfig.cfg.app.radar_cfg,
        ...appRadarConfig,
      };
      return saveAppConfig({ radar_cfg: newRadarConfig });
    },
    [remoteSensorAppConfig, saveAppConfig]
  );

  const updateOnScreenRadarAlertLocation = useCallback(
    async (_ev: any, value: string) => {
      if (!value) {
        return;
      }
      if (!remoteSensorAppConfig) {
        throw new Error("No remoteSensorAppConfig");
      }
      const newRadarConfig: WSMRemoteCfgStatusCfgAppRadar = {
        ...remoteSensorAppConfig.cfg.app.radar_cfg,
        alerts_on_screen_right_enabled: value === "Right",
      };
      return saveAppConfig({ radar_cfg: newRadarConfig });
    },
    [remoteSensorAppConfig, saveAppConfig]
  );

  const updateRadarAlertLocation = useCallback(
    async (_ev: any, value: string) => {
      if (!value) {
        return;
      }
      if (!remoteSensorAppConfig) {
        throw new Error("No remoteSensorAppConfig");
      }
      const newRadarConfig: WSMRemoteCfgStatusCfgAppRadar = {
        ...remoteSensorAppConfig.cfg.app.radar_cfg,
        alerts_on_left_leds_enabled: value === "Left LEDs",
        alerts_on_screen_enabled: value === "On screen",
      };
      return saveAppConfig({ radar_cfg: newRadarConfig });
    },
    [remoteSensorAppConfig, saveAppConfig]
  );

  const updateTirePressureLocation = useCallback(
    async (_ev: any, value: string, remoteSensorId: number) => {
      if (!value) {
        return;
      }
      saveRemoteSensorConfig(remoteSensorId, {
        sensor_location:
          value === "Front wheel" ? WSMSensorLocation.FRONT_WHEEL : WSMSensorLocation.REAR_WHEEL,
      });
    },
    [saveRemoteSensorConfig]
  );

  useEffect(() => {
    const listener = sensorManagerPlugin?.addListener(
      "boltCfgSensorCfgChangedEvent",
      ({ appToken: eventAppToken }) => {
        if (eventAppToken === appToken) {
          refreshRemoteSensorStatus();
        }
      }
    );
    return () => {
      (async () => (await listener)?.remove)();
    };
  }, [appToken, refreshRemoteSensorStatus, sensorManagerPlugin]);

  useEffect(() => {
    const listener = sensorManagerPlugin?.addListener(
      "boltCfgAppCfgChangedEvent",
      ({ appToken: eventAppToken }) => {
        if (eventAppToken === appToken) {
          mutateRemoteSensorAppConfig();
        }
      }
    );
    return () => {
      (async () => (await listener)?.remove)();
    };
  }, [appToken, mutateRemoteSensorAppConfig, sensorManagerPlugin]);

  return {
    device,
    remoteSensorStatus,
    productTypeInfoLookup,
    remoteSensorAppConfig,
    refreshRemoteSensorStatus,
    getRemoteSensor,
    saveDisplayName,
    getDisplayName,
    getImageSrc,
    forgetRemoteSensor,
    saveAppConfig,
    saveAppRadarConfig,
    updateOnScreenRadarAlertLocation,
    updateRadarAlertLocation,
    getWheelCircumferenceOptions,
    getShifterCfgOptions,
    saveRemoteSensorConfig,
    updateTirePressureLocation,
  };
};

export { useRemoteSensors };
